/**
 * Whether an Account's OnlyFans session is logged out, as the CRM view learns
 * it from gateway answers. The gateway relays OnlyFans' own verdict inside its
 * envelope ({ok:false, status_code, data}): a bare 401 ("Access denied.") or a
 * 400 with error code 301 ("Wrong user") means the login is dead, the same
 * signal the gateway uses to clear accounts.is_logged_in. Any successful
 * gateway answer for the Account clears it again.
 *
 * Every path that reaches the gateway feeds this one store: the React CRM
 * pages (crmClient, chat/api) directly, the CRM frame through
 * crm:account-session. CrmHostContext subscribes and shows the banner.
 */

type Listener = (accountId: string, loggedOut: boolean) => void;

const loggedOut = new Set<string>();
const listeners = new Set<Listener>();

/** The friendly text shown in place of OnlyFans' bare "Access denied.". */
export const LOGGED_OUT_MESSAGE = 'This account is logged out of OnlyFans. Log it in again to load it here.';

/** True for a gateway envelope that says the OnlyFans session is logged out. */
export function isLoggedOutEnvelope(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const b = body as { ok?: unknown; status_code?: unknown; data?: unknown };
  if (b.ok !== false) return false;
  if (b.status_code === 401) return true;
  const code = (b.data as { error?: { code?: unknown } } | null)?.error?.code;
  return b.status_code === 400 && code === 301;
}

/** Record what the Account's session looks like now; listeners hear changes only. */
export function setAccountLoggedOut(accountId: string, value: boolean): void {
  if (!accountId || loggedOut.has(accountId) === value) return;
  if (value) loggedOut.add(accountId);
  else loggedOut.delete(accountId);
  listeners.forEach((l) => l(accountId, value));
}

/** Read a gateway envelope for this Account: a logged-out verdict sets it, a success clears it. */
export function noteGatewayEnvelope(accountId: string, body: unknown): void {
  if (!accountId || !body || typeof body !== 'object') return;
  if (isLoggedOutEnvelope(body)) setAccountLoggedOut(accountId, true);
  else if ((body as { ok?: unknown }).ok === true) setAccountLoggedOut(accountId, false);
}

export function isAccountLoggedOut(accountId: string): boolean {
  return loggedOut.has(accountId);
}

export function subscribeAccountSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam: forget every Account. */
export function resetAccountSessions(): void {
  loggedOut.clear();
}
